import './OkulyonetimTabs.scss'

import {InputRef} from 'antd'
import Search from 'antd/es/input/Search'
import {Okul} from 'gql/StaticTypes'
import React, {useContext, useRef, useState} from 'react'
import {Link, useLocation, useNavigate} from 'react-router-dom'

import {AppRoutes} from '../../../App'
import AppContext from "../../../context/AppContext";
import {
    useOkulyonetimUrunlerSuggestsLazyQuery
} from "../../../gql/GeneratedGraphqlTypes";
import {useClickOutside} from "../../../hooks/useClickOutside";
import {ReactComponent as Digital} from './tabs-icons/digital.svg'
import {ReactComponent as EnglishBook} from './tabs-icons/engbook.svg'
import {ReactComponent as HomePage} from './tabs-icons/homepage.svg'
import {ReactComponent as ReadingBook} from './tabs-icons/readingbook.svg'
import {ReactComponent as SearchIcon} from './tabs-icons/search.svg'
import {ReactComponent as Shop} from './tabs-icons/shop.svg'
import {ReactComponent as TurkBook} from './tabs-icons/trbook.svg'
import {OkulYonetimTabsType} from './type'

interface OkulYonetimTabsProps {
    okul: Okul
}

const OkulyonetimTabs: React.FC<OkulYonetimTabsProps> = ({okul}) => {
    const navigate = useNavigate()
    const location = useLocation()
    const [searchValue, setSearchValue] = useState<string>('')
    const inputRef = useRef<InputRef | null>(null)
    const [getSuggestions, {data: suggestionsData}] = useOkulyonetimUrunlerSuggestsLazyQuery()
    const [suggestions, setSuggestions] = useState<{
        products: any[];
        categories: any[],
        brands: any[],
        classes: any[]
    }>({
        products: [],
        categories: [],
        brands: [],
        classes: [],
    })

    const {slug} = useContext(AppContext)
    const searchContainerRef = useRef<HTMLDivElement>(null)

    const fetchSuggestions = async (value: string) => {
        if (value.trim().length < 2) {
            setSuggestions({products: [], categories: [], brands: [], classes: []})
            return
        }

        try {
            const {data} = await getSuggestions({
                variables: {
                    filtre: {
                        arama: value,
                    },
                    slug: slug!,
                },
            })

            setSuggestions({
                products: [...(data?.okul?.okulyonetimUrunlerSuggests?.okulyonetim_urunler ?? [])],
                categories: [...(data?.okul?.okulyonetimUrunlerSuggests?.kategoriler ?? [])],
                brands: [...(data?.okul?.okulyonetimUrunlerSuggests?.yayinevleri ?? [])],
                classes: [...(data?.okul?.okulyonetimUrunlerSuggests?.siniflar ?? [])],
            })
        } catch (err) {
            console.error('Arama hatası:', err)
        }
    }
    const debounceTimer = useRef<NodeJS.Timeout | null>(null)

    useClickOutside(searchContainerRef, () => {
        setSearchValue('')
        setSuggestions({products: [], categories: [], brands: [], classes: []})
    })
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value
        setSearchValue(val)

        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current)
        }

        if (val.trim().length >= 2) {
            debounceTimer.current = setTimeout(() => {
                fetchSuggestions(val)
            }, 300)
        } else {
            setSuggestions({products: [], categories: [], brands: [], classes: []})
        }
    }

    const Tabs: OkulYonetimTabsType[] = [
        {
            url: `${AppRoutes.okulyonetim.foreignLanguageBoooks}`,
            icon: <EnglishBook className="tab-icon"/>,
            title: 'Yabancı Dil Kitapları',
            key: '1',
        },
        {
            url: `${AppRoutes.okulyonetim.turkishBoooks}`,
            icon: <TurkBook className="tab-icon"/>,
            title: 'Türkçe Kitaplar',
            key: '2',
        },
        {
            url: `${AppRoutes.okulyonetim.readingBoooks}`,
            icon: <ReadingBook className="tab-icon"/>,
            title: 'Okuma Kitapları',
            key: '3',
        },
        {
            url: `${AppRoutes.okulyonetim.stationary}`,
            icon: <Shop className="tab-icon"/>,
            title: 'Kırtasiye',
            key: '4',
        },
        {
            url: `${AppRoutes.okulyonetim.digitalProducts}`,
            icon: <Digital className="tab-icon"/>,
            title: 'Dijital Ürünler',
            key: '5',
        },
    ]

    const filteredTabs = Tabs.filter((tab) => okul.okul_kategorileri?.includes(tab.key))

    const handleSearch = (value: string) => {
        navigate(`/${AppRoutes.okulyonetim.base}/${AppRoutes.okulyonetim.search}?query=${value}`)
        setSearchValue('')
    }

    const handleSearchFocus = () => {
        inputRef.current?.focus()
    }

    return (
        <>
            <div className="db-okulyonetim-tabs">
                <div className="tabs-content">
                    <div
                        className={`other-tabs ${location.pathname === '/okulyonetim/' ? 'active-tab' : ''}`}
                        onClick={() => navigate('/okulyonetim/')}
                    >
                        <HomePage className="tab-icon"/>
                        Anasayfa
                    </div>

                    {filteredTabs.map((tab) => (
                        <div key={tab.url}
                             className={`tab-content ${location.pathname.endsWith(tab.url) ? 'active-tab' : ''}`}>
                            <Link to={tab.url} className="tab-content-link">
                                <div className="tab-logo">{tab.icon}</div>
                                <div className="tab-title">{tab.title}</div>
                            </Link>
                        </div>
                    ))}

                    <div className="other-tabs" onClick={handleSearchFocus}>
                        <SearchIcon className="tab-icon"/>
                        Ara
                    </div>
                </div>

                {location.pathname !== '/okulyonetim/' ? (
                    <div className="db-okulyonetim-search" ref={searchContainerRef}>
                        <Search
                            ref={inputRef}
                            className="search-input"
                            placeholder="Ne Aramıştınız? Ör. Ders kitabı adı, barkod, yayınevi veya seviye giriniz."
                            allowClear
                            width="100%"
                            enterButton
                            size="large"
                            value={searchValue}
                            onChange={handleInputChange}
                            onSearch={handleSearch}
                        />
                        {searchValue && suggestions && suggestionsData && (
                            <div className="search-dropdown">
                                <div className="search-dropdown-section">
                                    <h4>Ürünler</h4>
                                    {suggestions.products.slice(0, 5).map((product: any) => (
                                        <div className="search-dropdown-item" key={product.urun.isim} onClick={() =>
                                            window.open(
                                                `/v1/${AppRoutes.okulyonetim.base}/${AppRoutes.okulyonetim.productDetail}/${product.urun.id}`,
                                                '_blank',
                                                'noopener,noreferrer',
                                            )
                                        }>
                                            <img src={product.urun.resim} alt=""/>
                                            <span>{product.urun.isim}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="search-dropdown-section">
                                    <h4>İlgili Kategoriler</h4>
                                    {suggestions.categories.slice(0, 5).map((category: any) => (
                                        <div className="search-dropdown-item" key={category.kategori_slug} onClick={() =>
                                            window.open(
                                                `/v1/${AppRoutes.okulyonetim.base}/${AppRoutes.okulyonetim.search}?queryKategori=${category.kategori_slug}`,
                                                '_blank',
                                                'noopener,noreferrer',
                                            )
                                        }>
                                            {category.isim}
                                        </div>
                                    ))}
                                </div>
                                <div className="search-dropdown-section">
                                    <h4>İlgili Markalar</h4>
                                    {suggestions.brands.slice(0, 5).map((brand: any) => (
                                        <div className="search-dropdown-item" key={brand.yayinevi_slug} onClick={() =>
                                            window.open(
                                                `/v1/${AppRoutes.okulyonetim.base}/${AppRoutes.okulyonetim.search}?queryYayinevi=${brand.yayinevi_slug}`,
                                                '_blank',
                                                'noopener,noreferrer',
                                            )
                                        }>
                                            {brand.isim}
                                        </div>
                                    ))}
                                </div>
                                <div className="search-dropdown-section">
                                    <h4>İlgili Sınıflar</h4>
                                    {suggestions.classes.slice(0, 5).map((clas: any) => (
                                        <div className="search-dropdown-item" key={clas.sinif_slug} onClick={() =>
                                            window.open(
                                                `/v1/${AppRoutes.okulyonetim.base}/${AppRoutes.okulyonetim.search}?querySinif=${clas.sinif_slug}`,
                                                '_blank',
                                                'noopener,noreferrer',
                                            )
                                        }>
                                            {clas.isim}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                ) : null}

                {location.pathname === '/okulyonetim/' ? (
                    <div className="home-page-image">
                        <div className="home-info">
                            <span>Hoş geldiniz, Öğretmenim! 🌟 </span>
                            <p>
                                Geleceği şekillendiren kahramanlarımızdan biri olarak, öğrencileriniz için en iyi
                                kaynakları bulmanıza
                                yardımcı olmaktan mutluluk duyuyoruz. Başlamak için kategoriye göz atın veya arama
                                çubuğunu kullanarak
                                istediğiniz kitabı bulun!
                            </p>
                        </div>
                        <div className="db-okulyonetim-search-home" ref={searchContainerRef}>
                            <Search
                                ref={inputRef}
                                className="search-input"
                                placeholder="Ne Aramıştınız? Ör. Ders kitabı adı, barkod, yayınevi veya seviye giriniz."
                                allowClear
                                width="100%"
                                enterButton
                                size="large"
                                value={searchValue}
                                onChange={handleInputChange}
                                onSearch={handleSearch}
                            />
                            {searchValue && suggestions && suggestionsData && (
                                <div className="search-dropdown">
                                    <div className="search-dropdown-section">
                                        <h4>Ürünler</h4>
                                        {suggestions.products.slice(0, 5).map((product: any) => (
                                            <div className="search-dropdown-item" key={product.urun.isim} onClick={() =>
                                                window.open(
                                                    `/v1/${AppRoutes.okulyonetim.base}/${AppRoutes.okulyonetim.productDetail}/${product.urun.id}`,
                                                    '_blank',
                                                    'noopener,noreferrer',
                                                )
                                            }>
                                                <img src={product.urun.resim} alt=""/>
                                                <span>{product.urun.isim}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="search-dropdown-section">
                                        <h4>İlgili Kategoriler</h4>
                                        {suggestions.categories.slice(0, 5).map((category: any) => (
                                            <div className="search-dropdown-item" key={category.kategori_slug} onClick={() =>
                                                window.open(
                                                    `/v1/${AppRoutes.okulyonetim.base}/${AppRoutes.okulyonetim.search}?queryKategori=${category.kategori_slug}`,
                                                    '_blank',
                                                    'noopener,noreferrer',
                                                )
                                            }>
                                                {category.isim}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="search-dropdown-section">
                                        <h4>İlgili Markalar</h4>
                                        {suggestions.brands.slice(0, 5).map((brand: any) => (
                                            <div className="search-dropdown-item" key={brand.yayinevi_slug} onClick={() =>
                                                window.open(
                                                    `/v1/${AppRoutes.okulyonetim.base}/${AppRoutes.okulyonetim.search}?queryYayinevi=${brand.yayinevi_slug}`,
                                                    '_blank',
                                                    'noopener,noreferrer',
                                                )
                                            }>
                                                {brand.isim}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="search-dropdown-section">
                                        <h4>İlgili Sınıflar</h4>
                                        {suggestions.classes.slice(0, 5).map((clas: any) => (
                                            <div className="search-dropdown-item" key={clas.sinif_slug} onClick={() =>
                                                window.open(
                                                    `/v1/${AppRoutes.okulyonetim.base}/${AppRoutes.okulyonetim.search}?querySinif=${clas.sinif_slug}`,
                                                    '_blank',
                                                    'noopener,noreferrer',
                                                )
                                            }>
                                                {clas.isim}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                        </div>
                        <div className='image-1'></div>
                        <div className='image-2'></div>
                    </div>
                ) : null}
            </div>
        </>
    )
}

export default OkulyonetimTabs
